/**
 * Shared JD carousel: a native horizontal scroll-snap track with prev/next
 * arrows plus mouse drag-to-scroll. Generic, data-attribute driven so any JD
 * section can reuse it.
 *
 *   [data-jd-carousel-root]      wrapper holding the track + buttons
 *     [data-jd-carousel]         the scroll track (its direct children = slides)
 *     [data-jd-carousel-prev]    previous button (auto-hidden at the start)
 *     [data-jd-carousel-next]    next button (auto-hidden at the end)
 */
(function () {
  var DRAG_THRESHOLD = 5; /* px before a press becomes a drag rather than a click */

  function setup(track) {
    if (track.dataset.jdCarouselReady) return;
    track.dataset.jdCarouselReady = 'true';

    var root = track.closest('[data-jd-carousel-root]') || track.parentElement;
    var prev = root && root.querySelector('[data-jd-carousel-prev]');
    var next = root && root.querySelector('[data-jd-carousel-next]');

    function step() {
      var card = track.firstElementChild;
      if (!card) return track.clientWidth;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return card.getBoundingClientRect().width + gap;
    }

    function scrollable() {
      return track.scrollWidth > track.clientWidth + 1;
    }

    function update() {
      var max = track.scrollWidth - track.clientWidth - 1;
      if (prev) prev.hidden = !scrollable() || track.scrollLeft <= 0;
      if (next) next.hidden = !scrollable() || track.scrollLeft >= max;
    }

    function go(dir) {
      var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollBy({ left: dir * step(), behavior: reduce ? 'auto' : 'smooth' });
    }

    setupDrag(track, scrollable);

    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* Mouse drag-to-scroll (spec: desktop = native scroll with drag/swipe, no arrows).
     Touch/pen keep native scrolling — only mouse lacks a horizontal affordance. */
  function setupDrag(track, scrollable) {
    var down = false, dragging = false, startX = 0, startLeft = 0, cursorStyle = null;

    function onDown(e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if (!scrollable()) return;
      /* Let native controls (quick-add, wishlist, arrows) receive their own clicks. */
      if (e.target.closest('button')) return;
      down = true;
      dragging = false;
      startX = e.clientX;
      startLeft = track.scrollLeft;
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    }

    function onMove(e) {
      if (!down) return;
      var dx = e.clientX - startX;
      if (!dragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        dragging = true;
        /* Free-scroll during the drag; the mandatory snap resettles on release. */
        track.style.scrollSnapType = 'none';
        /* Force a grabbing cursor + kill selection/native drag over every child. */
        cursorStyle = document.createElement('style');
        cursorStyle.textContent = '*{cursor:grabbing !important;user-select:none !important}';
        document.head.appendChild(cursorStyle);
      }
      track.scrollLeft = startLeft - dx;
      e.preventDefault();
    }

    function onUp() {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      down = false;
      track.style.scrollSnapType = '';
      if (cursorStyle) { cursorStyle.remove(); cursorStyle = null; }
      if (dragging) {
        /* Swallow the click that a mouse fires after the drag so the card link
           (or any slide control) doesn't navigate on release. */
        var swallow = function (ev) { ev.preventDefault(); ev.stopPropagation(); };
        track.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(function () { track.removeEventListener('click', swallow, true); }, 0);
      }
      dragging = false;
    }

    track.addEventListener('pointerdown', onDown);
    /* Stop the browser's native image/link drag from pre-empting the scroll drag. */
    track.addEventListener('dragstart', function (e) { if (down) e.preventDefault(); });
  }

  function init(root) {
    (root || document).querySelectorAll('[data-jd-carousel]').forEach(setup);
  }

  /* Tracks that appear after load without a section-load event. The recommendations row is fetched
     by the native <product-recommendations> and morphed in hydration mode, which dispatches nothing
     — its track never reached setup(), so the mouse drag never bound and the desktop slider looked
     frozen while mobile kept scrolling natively (JDTMA-735). Only the added nodes are inspected,
     never the whole document, and setup() is idempotent, so a redundant call costs nothing. */
  function watch() {
    if (!window.MutationObserver) return;
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches('[data-jd-carousel]')) setup(node);
          else init(node);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* Exposed for markup that appears after load without a section render — the empty cart state is
     cloned from a <template> by component-cart-items.js, so it never goes through either event
     below. setup() is idempotent (data-jd-carousel-ready), so re-running init is free. */
  window.jdCarousel = { init: init };

  if (document.readyState !== 'loading') { init(); watch(); }
  else document.addEventListener('DOMContentLoaded', function () { init(); watch(); });
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();
